import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Flag, FolderOpen, FileText, ClipboardList, Info, Download, Eye,
  CheckCircle2, Clock, Target, FileArchive, User, Mail, Phone, Building2, MapPin, Server,
} from "lucide-react";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../lib/api";
import { useAuthStore } from "../../store/useAuthStore";
import InboxMilestoneModal from "./InboxMilestoneModal";

const money = (n?: number) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString()}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDay = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const fmtMonth = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—");

const MS_STYLE: Record<string, string> = {
  Released: "bg-secondary-container text-on-secondary-container",
  "In Escrow": "bg-amber-100 text-amber-700",
  "In Progress": "bg-secondary-container text-on-secondary-container",
  Pending: "bg-surface-container-highest text-on-surface-variant",
};

const fileTile = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return { Icon: FileArchive, cls: "bg-error-container/30 text-error" };
  return { Icon: FileText, cls: "bg-primary-container/30 text-primary" };
};

// Project-context sidebar for the Inbox — everything about the ACTIVE project:
// AI summary, current milestone, files, proposal, brief, scope, quick info.
export default function InboxProjectPanel({ project, onProjectUpdated }: { project: any; onProjectUpdated?: (p: any) => void }) {
  const navigate = useNavigate();
  const { authUser } = useAuthStore();
  const isClient = authUser?.role === "Client";
  // Milestone modal — opened from the card button or a system message's
  // "View Milestone" action (dispatched by ChatContainer).
  const [msIndex, setMsIndex] = useState<number | null>(null);

  const derived = useMemo(() => {
    const ms: any[] = project?.milestones || [];
    const total = ms.reduce((s, m) => s + (m.amount || 0), 0) || project?.projectPrice || 0;
    const released = ms.filter((m) => m.status === "Released").reduce((s, m) => s + (m.amount || 0), 0);
    const progress = total ? Math.min(100, Math.round((released / total) * 100)) : 0;
    const relCount = ms.filter((m) => m.status === "Released").length;
    const curIdx = ms.findIndex((m) => m.status !== "Released");
    const current = curIdx >= 0 ? ms[curIdx] : (ms[ms.length - 1] || null);
    const currentIdx = curIdx >= 0 ? curIdx : (ms.length ? ms.length - 1 : -1);
    const upcoming = curIdx >= 0 && curIdx + 1 < ms.length ? ms[curIdx + 1] : null;
    const lastReleased = [...ms].reverse().find((m) => m.status === "Released");
    return { ms, total, released, progress, relCount, current, currentIdx, upcoming, lastReleased };
  }, [project]);

  useEffect(() => {
    const h = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.milestoneIndex === "number") setMsIndex(detail.milestoneIndex);
    };
    window.addEventListener("cre:view-milestone", h);
    return () => window.removeEventListener("cre:view-milestone", h);
  }, []);

  if (!project) {
    return (
      <aside className="hidden lg:flex w-80 shrink-0 flex-col border-l border-outline-variant bg-surface-container-lowest items-center justify-center text-center p-6">
        <Info className="text-outline mb-2" size={22} />
        <p className="text-sm text-on-surface-variant">Select a project to see its context.</p>
      </aside>
    );
  }

  return (
    <aside className="hidden lg:flex w-80 shrink-0 flex-col border-l border-outline-variant bg-surface-container-lowest overflow-y-auto cre-scroll">
      {/* Client Information (top — shown to the project team, not the client themselves) */}
      {!isClient && project.client && (
        <div className="p-5 border-b border-outline-variant">
          <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><User size={18} className="text-primary" /> Client Information</h4>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center text-[14px] font-bold shrink-0">
              {(project.client.name || "?").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-on-surface truncate">{project.client.name || "—"}</p>
              {project.client.username && <p className="text-[12px] text-outline truncate">@{project.client.username}</p>}
            </div>
          </div>
          <div className="space-y-2.5">
            {project.client.company && (
              <div className="flex items-center gap-2.5 text-[13px] text-on-surface-variant"><Building2 size={15} className="text-outline shrink-0" /><span className="truncate">{project.client.company}</span></div>
            )}
            {project.client.email && (
              <a href={`mailto:${project.client.email}`} className="flex items-center gap-2.5 text-[13px] text-on-surface-variant hover:text-primary"><Mail size={15} className="text-outline shrink-0" /><span className="truncate">{project.client.email}</span></a>
            )}
            {project.client.phone && (
              <a href={`tel:${project.client.phone}`} className="flex items-center gap-2.5 text-[13px] text-on-surface-variant hover:text-primary"><Phone size={15} className="text-outline shrink-0" /><span className="truncate">{project.client.phone}</span></a>
            )}
            {project.client.location && (
              <div className="flex items-center gap-2.5 text-[13px] text-on-surface-variant"><MapPin size={15} className="text-outline shrink-0" /><span className="truncate">{project.client.location}</span></div>
            )}
            {project.client.timezone && (
              <div className="flex items-center gap-2.5 text-[13px] text-on-surface-variant"><Clock size={15} className="text-outline shrink-0" /><span className="truncate">{project.client.timezone.replace(/_/g, " ")}</span></div>
            )}
            {!project.client.email && !project.client.phone && !project.client.company && !project.client.location && (
              <p className="text-[12px] text-outline">No contact details on file.</p>
            )}
          </div>
        </div>
      )}

      {/* AI Project Summary */}
      <div className="p-5 border-b border-outline-variant">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface">
          <Sparkles size={18} className="text-primary" fill="currentColor" /> AI Project Summary
        </h4>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[12px] text-on-surface-variant">Current Progress</span>
              <span className="text-[12px] font-bold text-primary">{derived.progress}%</span>
            </div>
            <div className="h-2 w-full bg-surface-container rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${derived.progress}%` }} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={15} className="text-green-500 shrink-0 mt-0.5" />
              <p className="text-[13px] text-on-surface-variant"><span className="font-semibold">Recent:</span> {derived.lastReleased ? `${derived.lastReleased.title} approved` : "No milestone released yet"}</p>
            </div>
            <div className="flex items-start gap-2">
              <Clock size={15} className="text-outline shrink-0 mt-0.5" />
              <p className="text-[13px] text-on-surface-variant"><span className="font-semibold">Pending:</span> {derived.current ? derived.current.title : "—"}</p>
            </div>
          </div>
          <div className="bg-primary-container/10 p-3 rounded-lg border border-primary/20">
            <p className="text-[11px] text-primary font-bold uppercase tracking-wider mb-1">Next Recommended Step</p>
            <p className="text-[13px] text-on-surface-variant">{derived.current ? `Finalize ${derived.current.title}` : "Project complete"}</p>
          </div>
        </div>
      </div>

      {/* ERP Status (read-only — synced from ERP, never editable in CRE) */}
      {project.erp && (
        <div className="p-5 border-b border-outline-variant">
          <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><Server size={18} className="text-primary" /> ERP Status</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-on-surface-variant">Operational Status</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary-container/40 text-primary">{project.erp.status || "—"}</span>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1"><span className="text-[12px] text-on-surface-variant">ERP Progress</span><span className="text-[12px] font-bold text-primary">{project.erp.progress || 0}%</span></div>
              <div className="h-2 w-full bg-surface-container rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${project.erp.progress || 0}%` }} /></div>
            </div>
            {project.erp.projectManager && (
              <div className="flex justify-between"><span className="text-[11px] text-outline uppercase font-bold">Project Manager</span><span className="text-[13px] font-semibold text-on-surface">{project.erp.projectManager}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[11px] text-outline uppercase font-bold">ERP Project</span><span className="text-[12px] text-on-surface-variant">{project.erp.projectId}</span></div>
          </div>
        </div>
      )}

      {/* Milestone */}
      <div className="p-5 border-b border-outline-variant">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface">
          <Flag size={18} className="text-primary" /> Milestone
        </h4>
        <div className="space-y-4">
          {derived.current ? (
            <div className="bg-surface-container-low p-4 rounded-xl border border-outline-variant/30">
              <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Current Milestone</p>
              <p className="text-[14px] font-semibold text-on-surface">{derived.current.title}</p>
              <div className="flex justify-between mt-2">
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${MS_STYLE[derived.current.status] || "bg-surface-container-highest text-on-surface-variant"}`}>{derived.current.status}</span>
                <span className="text-[11px] text-outline">{derived.current.dueDate ? fmtDate(derived.current.dueDate) : money(derived.current.amount)}</span>
              </div>
            </div>
          ) : <p className="text-[13px] text-on-surface-variant">No milestones.</p>}
          {derived.upcoming && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Upcoming</p>
              <p className="text-[13px] text-on-surface-variant">{derived.upcoming.title}</p>
            </div>
          )}
          <button
            onClick={() => derived.currentIdx >= 0 ? setMsIndex(derived.currentIdx) : toast("No milestones on this project yet.")}
            className="w-full py-2 border border-primary text-primary font-semibold rounded-lg hover:bg-primary-container/10 transition-colors text-[13px]"
          >
            View Milestone
          </button>
        </div>
      </div>

      {/* Project Files */}
      <div className="p-5 border-b border-outline-variant">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-[14px] font-semibold flex items-center gap-2 text-on-surface"><FolderOpen size={18} className="text-primary" /> Project Files</h4>
          <button onClick={() => navigate(isClient ? "/portal/files" : "/bd/files")} className="text-[12px] text-primary font-semibold hover:underline">View All Files</button>
        </div>
        {(project.attachments || []).length === 0 ? (
          <p className="text-[13px] text-on-surface-variant">No files yet.</p>
        ) : (
          <div className="space-y-3">
            {(project.attachments || []).slice(0, 4).map((f: any) => {
              const { Icon, cls } = fileTile(f.name);
              const url = `${API_BASE_URL}/project/${project._id}/attachments/${f._id}/download`;
              return (
                <div key={f._id} className="flex items-center gap-3 p-2 hover:bg-surface-container rounded-lg cursor-pointer transition-colors group">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cls}`}><Icon size={20} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold truncate text-on-surface">{f.name}</p>
                    <p className="text-[11px] text-outline">{f.category || "File"} • {fmtDay(f.uploadedAt)}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <a href={url} target="_blank" rel="noreferrer" className="text-outline hover:text-primary"><Eye size={16} /></a>
                    <a href={url} target="_blank" rel="noreferrer" className="text-outline hover:text-primary"><Download size={16} /></a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Proposal Details */}
      <div className="p-5 border-b border-outline-variant">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><FileText size={18} className="text-primary" /> Proposal Details</h4>
        {project.proposal ? (
          <div className="space-y-4">
            <div>
              <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Proposal Name</p>
              <p className="text-[13px] font-semibold text-on-surface">{project.proposal.title}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Budget</p>
                <p className="text-[13px] font-semibold text-on-surface">{money(derived.total)}</p>
              </div>
              <div>
                <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Timeline</p>
                <p className="text-[13px] font-semibold text-on-surface">{project.eta ? fmtDate(project.eta) : "—"}</p>
              </div>
            </div>
            {project.proposal.publicToken && (
              <a href={`/proposal/public/${project.proposal.publicToken}`} target="_blank" rel="noreferrer" className="block text-center w-full py-2 border border-primary text-primary font-semibold rounded-lg hover:bg-primary-container/10 transition-colors text-[13px]">View Proposal</a>
            )}
          </div>
        ) : <p className="text-[13px] text-on-surface-variant">No proposal linked.</p>}
      </div>

      {/* Project Brief */}
      <div className="p-5 border-b border-outline-variant">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><ClipboardList size={18} className="text-primary" /> Project Brief</h4>
        <div className="space-y-4">
          <div>
            <p className="text-[11px] text-outline uppercase font-bold mb-1 tracking-wider">Original Requirements</p>
            <p className="text-[13px] text-on-surface-variant line-clamp-2">{project.description || "No brief provided."}</p>
          </div>
          <button onClick={() => navigate("/projects")} className="w-full py-2 bg-surface-container hover:bg-surface-container-high text-on-surface font-semibold rounded-lg transition-colors text-[13px]">View Complete Details</button>
        </div>
      </div>

      {/* Scope Management */}
      <div className="p-5 border-b border-outline-variant">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><Target size={18} className="text-primary" /> Scope Management</h4>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-on-surface-variant">Current Scope</span>
            <span className="text-[11px] px-2 py-0.5 bg-green-500/10 text-green-600 rounded-full font-bold">Stable</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-on-surface-variant">Pending Changes</span>
            <span className="text-[12px] text-outline">None</span>
          </div>
          <button onClick={() => toast("Scope update request routed to the project coordinator.", { icon: "📝" })} className="w-full py-2 bg-primary text-on-primary font-semibold rounded-lg hover:brightness-110 transition-all text-[13px]">Request Scope Update</button>
        </div>
      </div>

      {/* Quick Information */}
      <div className="p-5">
        <h4 className="text-[14px] font-semibold mb-4 flex items-center gap-2 text-on-surface"><Info size={18} className="text-primary" /> Quick Information</h4>
        <div className="space-y-4">
          {([
            ["Type", project.projectType],
            ["Start Date", fmtMonth(project.createdAt)],
            ["Expected", project.eta ? fmtMonth(project.eta) : "—"],
            ["Milestones", `${derived.relCount}/${derived.ms.length}`],
            ["Status", project.projectStatus],
            ["Coordinator", project.coordinator || "—", true],
          ] as [string, string, boolean?][]).map(([k, v, hl]) => (
            <div key={k} className="flex justify-between gap-2">
              <span className="text-[11px] text-outline uppercase font-bold">{k}</span>
              <span className={`text-[13px] font-semibold text-right truncate ${hl ? "text-primary" : "text-on-surface"}`}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {msIndex !== null && (
        <InboxMilestoneModal
          project={project}
          index={msIndex}
          onClose={() => setMsIndex(null)}
          onUpdated={(p) => { onProjectUpdated?.(p); }}
        />
      )}
    </aside>
  );
}
